/** SUNTRONIX ONE — Tenant portal: My Orders & Deliveries (+ approvals).
 *  Order lifecycle timeline, courier/AWB tracking, PO upload, and tenant
 *  internal approvals for orders that exceed the approval limit. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import OneFlow from "@/components/oneflow/OneFlow";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Package, Loader2, Truck, CheckCircle2, XCircle, Upload, Clock, MapPin, ChevronRight, ShieldCheck,
} from "lucide-react";

const money = (n, cur = "INR") => n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;
const TINT = {
  PENDING_APPROVAL: "bg-amber-50 text-amber-700", REJECTED: "bg-rose-50 text-rose-700",
  ORDER_RECEIVED: "bg-sky-50 text-sky-700", PO_VERIFIED: "bg-sky-50 text-sky-700",
  PROCESSING: "bg-indigo-50 text-indigo-700", READY_TO_DISPATCH: "bg-indigo-50 text-indigo-700",
  DISPATCHED: "bg-violet-50 text-violet-700", DELIVERED: "bg-emerald-50 text-emerald-700",
  COMPLETED: "bg-emerald-50 text-emerald-700",
};

function OrderDetail({ order, onClose }) {
  const o = order;
  const idx = o.flow?.indexOf(o.status) ?? -1;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="order-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-sky-600" />{o.order_no}</DialogTitle>
          <div className="text-[12px] text-slate-500">{o.status_label} · {money(o.subtotal, o.currency)}{o.po_number ? ` · PO ${o.po_number}` : ""}</div>
        </DialogHeader>
        <OneFlow flowType="ORDER" recordId={o.id} audience="tenant" title="Order journey" />
        <div className="overflow-y-auto flex-1 min-h-0 space-y-4">
          {o.status !== "PENDING_APPROVAL" && o.status !== "REJECTED" && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="order-timeline">
              {o.flow.map((st, i) => (
                <div key={st} className="flex items-center gap-1">
                  <div className={"text-[10px] px-2 py-1 rounded-full " + (i <= idx ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-400")}>{st.replace(/_/g, " ")}</div>
                  {i < o.flow.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
                </div>
              ))}
            </div>
          )}
          {(o.shipments || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3" data-testid="order-shipments">
              <div className="text-[13px] font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5"><Truck className="h-4 w-4 text-violet-600" />Delivery</div>
              {o.shipments.map((s, i) => (
                <div key={i} className="text-[12px] text-slate-600 flex flex-wrap gap-x-4">
                  <span>Courier: <b>{s.courier || "—"}</b></span><span>AWB: <b>{s.awb || "—"}</b></span>
                  <span>ETA: {s.eta || "—"}</span><span className="text-slate-400">{s.status}</span>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[12px] text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="whitespace-pre-line">{o.ship_to}</span></div>
            <div className="mt-2 divide-y divide-slate-50">
              {o.items.map((it) => (
                <div key={it.product_code} className="flex justify-between py-1.5 text-[12px]">
                  <span className="text-slate-700">{it.product_name} <span className="text-slate-400">× {it.quantity}</span></span>
                  <span className="text-slate-600">{it.pricing?.mode === "PRICE" ? money(it.line_total, o.currency) : "RFQ"}</span>
                </div>
              ))}
            </div>
          </div>
          {(o.assets || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3" data-testid="order-assets">
              <div className="text-[13px] font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" />Delivered assets</div>
              {o.assets.map((a, i) => (
                <div key={i} className="text-[12px] text-slate-600">{a.product_name} · Serial <b>{a.serial}</b>{a.assigned_user ? ` · ${a.assigned_user}` : ""}</div>
              ))}
            </div>
          )}
          {(o.timeline || []).length > 0 && (
            <div className="text-[11px] text-slate-400 space-y-0.5">
              {o.timeline.map((t, i) => <div key={i}>{new Date(t.at).toLocaleString()} — {t.event}{t.note ? ` (${t.note})` : ""}</div>)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [approvals, setApprovals] = useState({ items: [], can_approve: false });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, a] = await Promise.all([api.get("/tenant-catalogue/my-orders"), api.get("/tenant-catalogue/my-approvals")]);
      setOrders(o.data.items); setApprovals(a.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load orders"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try { await api.post(`/tenant-catalogue/my-orders/${id}/approve`, { decision }); toast.success(decision === "APPROVE" ? "Order approved" : "Order rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const uploadPo = async (id, file) => {
    if (!file) return; setUploading(id);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/tenant-catalogue/my-orders/${id}/po`, fd); toast.success("PO uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(null); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="my-orders-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Procurement</div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Package className="h-6 w-6 text-sky-600" />My Orders</h1>
        <p className="text-sm text-slate-500">Track your orders, deliveries and approvals.</p>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <>
          {approvals.can_approve && approvals.items.length > 0 && (
            <div className="space-y-2" data-testid="approvals-section">
              <div className="text-[13px] font-semibold text-amber-700 flex items-center gap-1.5"><Clock className="h-4 w-4" />Awaiting your approval</div>
              {approvals.items.map((o) => (
                <div key={o.id} data-testid={`approval-${o.id}`} className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{o.order_no} · {money(o.subtotal, o.currency)}</div>
                    <div className="text-[12px] text-slate-500">by {o.placed_by_name} · {o.items.length} item(s)</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => decide(o.id, "REJECT")} data-testid={`reject-${o.id}`} className="text-rose-600"><XCircle className="h-3.5 w-3.5 mr-1" />Reject</Button>
                  <Button size="sm" onClick={() => decide(o.id, "APPROVE")} data-testid={`approve-${o.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {orders.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No orders yet.</div>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="rounded-lg border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3">
                <button onClick={() => setDetail(o)} className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-slate-800">{o.order_no} <span className="text-[11px] text-slate-400">· {o.items.length} item(s)</span></div>
                  <div className="text-[12px] text-slate-500">{money(o.subtotal, o.currency)} · {o.ship_to}{o.shipments?.length ? ` · AWB ${o.shipments[o.shipments.length-1].awb || "—"}` : ""}</div>
                </button>
                <Badge className={TINT[o.status] || "bg-slate-100 text-slate-600"}>{o.status_label}</Badge>
                <label className="cursor-pointer inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50" data-testid={`order-po-${o.id}`}>
                  {uploading === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}PO
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(ev) => { uploadPo(o.id, ev.target.files?.[0]); ev.target.value = ""; }} />
                </label>
                <Button size="sm" variant="outline" onClick={() => setDetail(o)} data-testid={`order-view-${o.id}`}>Track</Button>
              </div>
            ))}
          </div>
        </>
      )}
      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
